import React, { useRef, useState, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { classNames } from "../../../../../helpers/classNames";
import { LuGripVertical } from "react-icons/lu";
import { EyeIcon, EyeOffIcon } from "@heroicons/react/outline";

const ColumnOrderRow = ({
    column,
    index,
    isHidden,
    onToggleVisibility,
    onMoveColumn,
    totalColumns,
    width,
    onWidthChange
}) => {
    const ref = useRef(null);

    // Use default_width from column definition if no custom width is provided
    const defaultWidth = column.default_width || 200;
    const effectiveWidth = width || defaultWidth;
    const isUsingDefault = !width || width === defaultWidth;

    const [localWidth, setLocalWidth] = useState(effectiveWidth);

    // Sync localWidth when width prop or default changes
    useEffect(() => {
        setLocalWidth(effectiveWidth);
    }, [effectiveWidth]);

    const [{ handlerId }, drop] = useDrop({
        accept: 'column',
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            onMoveColumn(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: 'column',
        item: () => {
            return { id: column.col_id, index };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0.4 : 1;
    drag(drop(ref));

    const handleToggleVisibility = () => {
        onToggleVisibility(column.col_id);
    };

    const handleWidthChange = (e) => {
        const newWidth = parseInt(e.target.value) || 200;
        setLocalWidth(newWidth);
        onWidthChange(column.col_id, newWidth);
    };

    const handleWidthBlur = () => {
        // Ensure minimum width of 100px
        const clampedWidth = Math.max(100, localWidth);
        if (clampedWidth !== localWidth) {
            setLocalWidth(clampedWidth);
            onWidthChange(column.col_id, clampedWidth);
        }
    };

    const handleWidthAdjustment = (adjustment) => {
        const newWidth = Math.max(100, localWidth + adjustment);
        setLocalWidth(newWidth);
        onWidthChange(column.col_id, newWidth);
    };

    return (
        <div
            ref={ref}
            style={{ opacity }}
            data-handler-id={handlerId}
            className={classNames(
                "flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md transition-all duration-200",
                isDragging && "cursor-grabbing",
                isHidden && "opacity-60"
            )}
        >
            <div className="flex items-center space-x-2 flex-1">
                {/* Order Number */}
                <div className="w-6 h-6 bg-slate-100 text-slate-700 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {index + 1}
                </div>

                {/* Drag Handle */}
                <div
                    className="cursor-grab hover:bg-gray-50 p-0.5 rounded transition-colors duration-150"
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                >
                    <LuGripVertical className="w-4 h-4 text-gray-400" />
                </div>

                {/* Column Info */}
                <div className="flex-1 min-w-0">
                    <p className={classNames(
                        "text-sm font-medium truncate",
                        isHidden ? "text-gray-500" : "text-gray-900"
                    )}>
                        {column.title}
                    </p>

                </div>
            </div>

            <div className="flex items-center space-x-2">
                {/* Width Input */}
                <div className="flex flex-col items-center">
                    <div className="flex items-center space-x-1">
                        <label className="text-xs text-gray-500 mr-1">Width</label>
                        <button
                            onClick={() => handleWidthAdjustment(-50)}
                            className="w-5 h-5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded text-xs font-medium flex items-center justify-center transition-colors duration-150"
                            title="Decrease width by 50px"
                            disabled={localWidth <= 100}
                        >
                            -
                        </button>
                        <input
                            type="number"
                            value={localWidth}
                            onChange={handleWidthChange}
                            onBlur={handleWidthBlur}
                            min="100"
                            max="500"
                            className={classNames(
                                "w-14 px-1 py-0.5 text-xs border rounded focus:ring-1 focus:ring-blue-500",
                                isUsingDefault
                                    ? "border-gray-300 bg-gray-50 text-gray-600"
                                    : "border-blue-300 bg-white text-gray-900"
                            )}
                            placeholder={defaultWidth.toString()}
                        />
                        <button
                            onClick={() => handleWidthAdjustment(50)}
                            className="w-5 h-5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded text-xs font-medium flex items-center justify-center transition-colors duration-150"
                            title="Increase width by 50px"
                        >
                            +
                        </button>
                        <span className="text-xs text-gray-400">px</span>
                    </div>
                </div>

                {/* Visibility Toggle */}
                <button
                    onClick={handleToggleVisibility}
                    className={classNames(
                        "p-1 rounded-md transition-colors duration-150 flex-shrink-0",
                        isHidden
                            ? "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                            : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    )}
                    title={isHidden ? "Show column" : "Hide column"}
                >
                    {isHidden ? (
                        <EyeOffIcon className="w-4 h-4" />
                    ) : (
                        <EyeIcon className="w-4 h-4" />
                    )}
                </button>
            </div>
        </div>
    );
};

export default ColumnOrderRow; 